import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, once } from '@web/utils';
import { Observable } from 'rxjs';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorResponse } from '@angular/common/http';
import { AdminPaymentMethodFormData } from '../../models/admin-payment-method-form-data';
import { AdminPaymentMethodDataSourceService } from '../../services/admin-payment-method-data-source.service';
import { AdminPaymentMethodFormComponent } from '../admin-payment-method-form/admin-payment-method-form.component';
import { SummernoteImageUpload } from '@web/shared/external';

@Component({
  selector: 'app-admin-payment-method-edit',
  imports: [
    CommonModule,
    AlertsComponent,
    SpinnerFilledComponent,
    AdminPaymentMethodFormComponent,
  ],
  templateUrl: './admin-payment-method-edit.component.html',
  styleUrl: './admin-payment-method-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminPaymentMethodEditComponent {
  /* prettier-ignore */
  private readonly paymentMethodDataSource = inject(AdminPaymentMethodDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output();

  protected data$!: Observable<AdminPaymentMethodFormData>;
  /* prettier-ignore */
  protected readonly uploadFn = (files: FileList): Observable<SummernoteImageUpload[]> => this.paymentMethodDataSource.uploadEditorImages(Array.from(files));

  public constructor() {
    effect(() => this.configureDataSource());
  }

  public handleSubmitSuccessEvent(data: AdminPaymentMethodFormData): void {
    this.paymentMethodDataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => this.notifySubmitSuccessEvent(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private configureDataSource(): void {
    this.data$ = this.paymentMethodDataSource.fetchDetailsFormById(this.id());

    this.changeDetectorRef.detectChanges();
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    this.alerts.showMultiple((builder) =>
      this.httpError
        .extract(response)
        .messages()
        .map((message) => builder.buildFailure(message)),
    );
  }
}
